import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useErrorMessage } from '../hooks/useErrorMessage';
import { selectUser } from '../../slices/user/userSlice';
import { addDocumentContract } from '../../slices/document-contracts/documentContractsSlice';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { AIDocumentContractModel } from '../../models/ai.document.contract.model';
import { NWClient } from '../../client/NWClient';
import { ROUTES } from '../constants/routes';
import { Helpers } from '../helpers/helpers';
import { BlankDocumentModalProps } from './types';

export const BlankDocumentModal: React.FC<BlankDocumentModalProps> = ({
  isBlankModalOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [documentName, setDocumentName] = useState(
    () => `${t('buttons.newDocument')} ${Helpers.timeStampToDateV4(new Date().toISOString())}`
  );
  const [documentCategory, setDocumentCategory] = useState('business');

  const categoryOptions = [
    { id: 'blankDocumentModalEmploymentMenuItem', value: 'employment' },
    { id: 'blankDocumentModalCustomersMenuItem', value: 'customers' },
    { id: 'blankDocumentModalSuppliersMenuItem', value: 'suppliers' },
    { id: 'blankDocumentModalBusinessMenuItem', value: 'business' },
  ];

  const handleBlankDocumentCreation = (newname: string, category: string) => {
    const blankDocData = {
      data: {
        user: currentUser.id,
        name: newname,
        category: category,
      },
    };
    dispatch(showOverlay());
    dispatch(addDocumentContract(blankDocData))
      .unwrap()
      .then((res) => {
        const documentContract = res as AIDocumentContractModel;
        const data = {
          user: currentUser.id,
          html: `<div>
    <h1  style="margin-bottom: 3pt; text-align: center; break-inside: avoid; break-after: avoid; line-height: 115%; font-size: 26pt;"><span style="font-size: 24px;">${t(
      'blankDocumentModal.defaultDocumentTitle'
    )}</span></h1>
    <p><span>${t('blankDocumentModal.defaultDocumentParagraph')}</span></p>
</div>`,
        };
        NWClient.patch(`document-contract/${documentContract.id}/`, null, data).finally(() => {
          dispatch(hideOverlay());
          navigate(
            `${ROUTES.DOCUMENT_VAULT}/${documentContract.id}?initialTab=0&contractGenerated=true`
          );
        });
      })
      .catch((error) => {
        dispatch(hideOverlay());
        onClose();
        if (error.errors?.length > 0 && error.errors[0] === t('messages.exceededPlanLimits')) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          toast.error(getErrorMessage(error));
        }
      })
      .finally(() => {
        onClose();
      });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newDocumentName = formData.get('newDocumentName') as string;
    const category = formData.get('category') as string;
    handleBlankDocumentCreation(newDocumentName, category);
  };

  return (
    <Modal
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
      open={isBlankModalOpen}
      onClose={onClose}
    >
      <Grid component='div' container sx={{ width: '70%' }}>
        <Grid component='div' sx={{ width: '100%' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant={'h6'} gutterBottom>
                {t('blankDocumentModal.title')}
              </Typography>

              <Typography>{t('blankDocumentModal.description')}</Typography>
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    marginTop: '0.5rem',
                  }}
                >
                  <TextField
                    id='blankDocumentModalDocumentNameInput'
                    name='newDocumentName'
                    placeholder={t('blankDocumentModal.documentNamePlaceholder')}
                    label={t('blankDocumentModal.documentNameLabel')}
                    variant='outlined'
                    fullWidth
                    required={true}
                    value={documentName}
                    slotProps={{
                      input: {
                        sx: { boxSizing: 'border-box', height: '48px !important' },
                      },
                    }}
                    onChange={(e) => setDocumentName(e.target.value)}
                  />
                  <FormControl fullWidth>
                    <InputLabel required={true} id='blankDocumentModalCategorySelectLabel'>
                      {t('labels.category')}
                    </InputLabel>
                    <Select
                      labelId='blankDocumentModalCategorySelectLabel'
                      id='blankDocumentModalCategorySelect'
                      name='category'
                      label={t('labels.category')}
                      required={true}
                      value={documentCategory}
                      sx={{
                        boxSizing: 'border-box',
                        height: '48px !important',
                        paddingTop: '12px',
                      }}
                      onChange={(e) => setDocumentCategory(e.target.value)}
                    >
                      {categoryOptions.map(({ id, value }) => (
                        <MenuItem key={value} id={id} value={value}>
                          {t(`tagsLabels.categories.${value}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Grid
                  component='div'
                  container
                  style={{
                    justifyContent: 'flex-end',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginTop: '16px',
                  }}
                >
                  <Button
                    id='blankDocumentModalGoBackBtn'
                    variant='contained'
                    onClick={onClose}
                    className={'btn-default btn-action-basic btn-cancel'}
                  >
                    {t('buttons.goBack')}
                  </Button>

                  <Button
                    id='blankDocumentModalCreateBtn'
                    variant='contained'
                    type='submit'
                    className='btn-default btn-action-basic'
                    disabled={!documentCategory || !documentName.trim()}
                  >
                    {t('buttons.create')}
                  </Button>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default BlankDocumentModal;
